import { useEffect, useState } from 'react'

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  cancelIdleCallback?: (handle: number) => void
}

/**
 * Returns true only AFTER the first paint / window load, so non-critical work
 * (timers, scroll listeners, below-the-fold logic) never blocks the hero render.
 */
export function useDeferredReady(): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const win = window as IdleWindow
    let idleHandle: number | undefined
    let timeoutHandle: number | undefined

    const start = () => {
      if (win.requestIdleCallback) {
        idleHandle = win.requestIdleCallback(() => setReady(true), { timeout: 1200 })
      } else {
        timeoutHandle = window.setTimeout(() => setReady(true), 200)
      }
    }

    if (document.readyState === 'complete') {
      start()
      return () => {
        if (idleHandle !== undefined) win.cancelIdleCallback?.(idleHandle)
        if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle)
      }
    }

    window.addEventListener('load', start, { once: true })
    return () => {
      window.removeEventListener('load', start)
      if (idleHandle !== undefined) win.cancelIdleCallback?.(idleHandle)
      if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle)
    }
  }, [])

  return ready
}
